import React from 'react';
import PropTypes from 'prop-types';
import css from './SvgIcon.module.css';

/**
 * Renders an SVG asset as a styled <span> with a background-image.
 * Right-clicking does not expose the "Save image as..." menu since there is no <img> element.
 *
 * Defaults to 1em sizing. Callers can override with className, width/height, or style.
 *
 * @param {string} src        - Image asset URL (typically imported SVG)
 * @param {string} [alt]      - Accessible label alias
 * @param {string} [aria-label] - Accessible label
 * @param {string} [className]
 * @param {Object} [style]
 * @param {string|number} [width]
 * @param {string|number} [height]
 */
const SvgIcon = React.forwardRef(function SvgIcon(
  { src, alt = '', className, style, width, height, 'aria-label': ariaLabel, ...rest },
  ref
) {
  const accessibleLabel = ariaLabel || alt;
  const inlineStyle = {
    backgroundImage: src ? `url(${src})` : undefined,
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
    ...style,
  };

  return (
    <span
      ref={ref}
      className={[css.svgIcon, className].filter(Boolean).join(' ')}
      style={inlineStyle}
      role={accessibleLabel ? 'img' : undefined}
      aria-label={accessibleLabel || undefined}
      aria-hidden={accessibleLabel ? undefined : true}
      {...rest}
    />
  );
});

SvgIcon.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  'aria-label': PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SvgIcon;
